// https://github.com/facebook/react/issues/3386#issuecomment-692412369

import { CaseRunStatus, IdNameModel, ParameterReadModel, ParameterTypes, RunParameterModel } from "@app/api2/api";
import Decimal from "decimal.js";
import humanizeDuration from "humanize-duration";
import moment from "moment";
import React from "react";
import { Link } from "react-router-dom";
import { buildUrl, AppRoutes } from "../AppRoutes";
import { BPColumn } from "./BPReactTable";
import { LabsFilter } from "./LabsFilter";
import { FilterProps, Renderer } from "react-table";
import { FieldPath } from "./KeyOfHelpers2";
import { customAlphabet } from "nanoid";
import { LabsDropdown } from "./LabsDropdown";
import { ColumnDef } from "@tanstack/react-table";
import { BPCheckbox } from "@/components/bp/bpcheckbox";

// modified to split on patterns
export const insertComponentsIntoText = (
    str: string,
    replacements: {
        [key: string]: React.ReactNode
    }
) => {
    const splitRegex = new RegExp(`(${Object.keys(replacements).join('|')})`, 'g');
    const parts = str.split(splitRegex);

    return parts.map(part => {
        if (replacements.hasOwnProperty(part)) {
            return replacements[part];
        }
        return part;
    });
};

const reduceJsxToString = (previous: string, current: React.ReactNode): string =>
    previous + innerText(current);


const hasProps = (jsx: React.ReactNode): jsx is React.ReactElement =>
    Object.prototype.hasOwnProperty.call(jsx, 'props');

export const innerText = (jsx: React.ReactNode): string => {

    // Empty
    if (
        jsx === null ||
        typeof jsx === 'boolean' ||
        typeof jsx === 'undefined'
    ) {
        return '';
    }

    // Numeric children.
    if (typeof jsx === 'number') {
        return jsx.toString();
    }

    // String literals.
    if (typeof jsx === 'string') {
        return jsx;
    }

    // Array of JSX.
    if (Array.isArray(jsx)) {
        return jsx.reduce(reduceJsxToString, '');
    }

    // Children prop.
    if (
        hasProps(jsx) &&
        Object.prototype.hasOwnProperty.call(jsx.props, 'children')
    ) {
        return innerText(jsx.props.children);
    }

    // Default
    return '';
};


export function toLocalTime(datetime) {
    return datetime ? moment(datetime).locale(window.locale).format("l LTS") : "";
}

export function toDateTime(datetime) {
    return datetime ? moment(datetime).format("l LTS") : "";
}

export function toDate(datetime) {
    return datetime ? moment(datetime).format("l") : "";
}

export function toPartialDate(year, month, day) {
    if (year == null) return "";
    if (month == null) month = 1;
    if (day == null) day = 1;
    return moment(`${year}-${month}-${day}`, "YYYY-M-D").format("l");
    // [sample.birthYear != null ? sample.birthYear : '', sample.birthMonth != null ? sample.birthMonth : '', sample.birthDay != null ? sample.birthDay : ''].filter(x => x !== '').join('/')
}

export function yesNo(value) {
    return value == true || value == 1 || value == '1' ? window.translation.common.yes : window.translation.common.no;
}

export function toSci(value, precision = 4, ifNull = '') {
    if (value == null)
        return ifNull;

    return parseFloat(value).toPrecision(precision);
}

export function toSciDecimal(value: Decimal, precision = 4) {
    if (value == null)
        return 'N/A';

    return value.toPrecision(precision);
}

export function anyToDecimal(value) {
    if (value == null || isNullOrWhitespace(value))
        return null;

    if (value == "N/A")
        return null;

    return new Decimal(value);
}


export function typedName<T extends unknown>(name: keyof T) {
    return name;
}

export function typedName2<T extends unknown, K extends keyof T>(source: T, property: K) {
    return property;
}

export function listToObj<T>(list: Array<T>, propName: (item: T) => string, value: (item: T) => any) {
    let v = {};

    for (let p of list) {
        v[propName(p)] = value(p);
    }

    return v;
}


export const replaceInString = (
    str: string,
    replacements: {
        [key: string]: any
    }
) => {
    return replaceInStringElement(str, replacements).join('');
};


export const replaceInStringElement = (
    str: string,
    replacements: {
        [key: string]: any
    }
) => {
    const splitRegex = new RegExp(`(${Object.keys(replacements).join('|')})`, 'g');
    const parts = str.split(splitRegex);

    const result = parts.map(part => {
        if (replacements.hasOwnProperty(part)) {
            return (replacements[part] ?? '');
        }
        return part;
    })

    return result;
};

export function isNullOrWhitespace(value: string) {
    return value == null || value?.toString().trim() == '';
}

// Checks if an object has no keys or all values are null/undefined/empty/whitespace
export function isNullEmptyObject(keyboardSample: any) {
    return keyboardSample == null || keyboardSample == undefined || Object.keys(keyboardSample).length === 0 || Object.values(keyboardSample).every(v => v === null || v === undefined || (Array.isArray(v) && v.length === 0) || (typeof v === 'string' && v.trim() === ''));
}

export function selectMany<TIn, TOut>(input: TIn[], selectListFn: (t: TIn) => TOut[]): TOut[] {
    return input.reduce((out, inx) => {
        out.push(...selectListFn(inx));
        return out;
    }, new Array<TOut>());
}

// https://reactjs.org/blog/2015/12/16/ismounted-antipattern.html
// https://github.com/facebook/react/issues/5465#issuecomment-157888325
export function makeCancelable<T>(promise: Promise<T>) {
    let hasCanceled_ = false;

    const wrappedPromise = new Promise<T>((resolve, reject) => {
        promise.then(
            val => hasCanceled_ ? reject({ isCanceled: true }) : resolve(val),
            error => hasCanceled_ ? reject({ isCanceled: true }) : reject(error)
        );
    });

    return {
        promise: wrappedPromise,
        cancel() {
            hasCanceled_ = true;
        },
    };
}

export function elapsedSecToString(seconds: number) {
    let locale = localStorage.getItem("locale").split('-')[0]
    return humanizeDuration(seconds * 1000, { maxDecimalPoints: 2, language: locale, fallbacks: ["en"] });
}

export function elapsedMinToString(minutes: number) {
    return elapsedSecToString(minutes * 60);
}

export const downloadBlob = (blobInfo: { data: Blob, fileName: string }) => {
    var a = document.createElement("a");
    a.href = window.URL.createObjectURL(blobInfo.data);
    a.download = blobInfo.fileName;
    a.click();
}

export function bytesToGb(value: number) {
    return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function bytesToMb(value: number) {
    return `${(value / 1024 / 1024).toFixed(2)} MB`;
}


export function parseParameters<T>(parameters: ParameterReadModel[]): T {
    var retObject = {} as T;

    for (let p of parameters) {
        switch (p.parameterType) {
            case ParameterTypes.String:
                retObject[p.parameter] = p.value;
                break;
            case ParameterTypes.Json:
                retObject[p.parameter] = JSON.parse(p.value);
                break;
            case ParameterTypes.Integer:
                retObject[p.parameter] = parseInt(p.value);
                break;
            case ParameterTypes.Number:
                retObject[p.parameter] = parseFloat(p.value);
                break;
            case ParameterTypes.Boolean:
                retObject[p.parameter] = p.value == "1";
                break;
        }
    }

    return retObject;
}

export function getRunStatusAsString(status: CaseRunStatus) {
    return window.translation.caseRunStatus[CaseRunStatus[status]?.toLowerCase()];
}

export function normalizeSystemName(sysName: string) {
    return sysName.split(/[_ ]/).join(' ').toUpperCase();
}

export function convertCasesToLinks(cases: IdNameModel[], labId: number) {
    return <>{
        cases?.map((c, idx) => (
            <React.Fragment key={c.id}>
                {!!idx && <span>,&nbsp;</span>}
                {<Link to={buildUrl(AppRoutes.CaseDetails, { labId: labId, caseId: c.id })}>{c.name}</Link>}
            </React.Fragment>
        ))
    }</>
}

export function getInactiveFilter<T>(name: string, label: string) {
    let a = {
        Filter: (filter) => <BPCheckbox label={label} checked={filter.column.filterValue} onChange={(e, { checked }) => filter.setFilter(filter.column.id, checked)} />,
        Header: '',
        id: name,
        disableSortBy: true,
        width: "0px",
        hidden: true
    } as BPColumn<T>;
    return a;
}

export function getInactiveFilter2<T>(name: string, label: string) {
    let a = {
        id: name,
        header: label,
        meta: {
            Filter: f => <BPCheckbox label={label} checked={f.getFilterValue() as boolean} onChange={(e, { checked }) => f.setFilterValue(checked)} />,
            width: "0px",
            hidden: true
        }
    }as ColumnDef<T>;

    return a;
}


export function getLabFilter2<T>(name: string, label: string) {
    let a = {
        id: name,
        header: label,
        meta: {
            Filter: f => <LabsDropdown onChange={(ev, data) => {
                f.setFilterValue(data.value);
            }}></LabsDropdown>,
            width: "0px",
            hidden: true
        },
    };

    return a;
}

export function getLabFilter<T>({ name, label, hideIf = false }: { name: string, label: string, hideIf?: boolean }) {
    // const user = useUser();
    let a = {
        Filter: f => !hideIf && <LabsFilter filter={f} />,

        Header: '',
        id: name,
        disableSortBy: true,
        width: "0px",
        hidden: true
    } as BPColumn<T>;
    return a;
}

export function getEmptyFilter<T>(name: string, label: string, Filter: Renderer<FilterProps<any>>) {
    let a = {
        Filter: Filter,
        Header: '',
        defaultFilterValue: true,
        id: name,
        disableSortBy: true,
        width: "0px",
        hidden: true
    } as BPColumn<T>;
    return a;
}

// const event = new CustomEvent("sessionSet", { detail: elem.dataset.time });
export class SessionSetEvent extends Event {
    constructor(public key, public newValue) {
        super("sessionSet");
    }
}

export const sessionGet = (key?: string) => {
    if (key == null)
        return null;

    var item = sessionStorage.getItem(key);
    try {
        console.debug(`Reading from localStorage: ${key} ${item}`);
        return JSON.parse(item);
    } catch {
        return null;
    }
}

export const sessionSet = (key: string, newValue: any) => {
    var value = window.sessionStorage.setItem(key, JSON.stringify(newValue));
    // Manually dispatch a storage event for same-tab listeners
    window.dispatchEvent(
        new StorageEvent("storage", {
            key,
            newValue: JSON.stringify(newValue),
            oldValue: JSON.stringify(value),
            storageArea: window.sessionStorage,
        })
    );

    return newValue;
}

export class NameOf<T> {
    getFieldName: (s: keyof T) => string = (s) => s.toString();
}

export type NestedKeyOf<T, K = keyof T> = K extends keyof T & (string | number)
    ? `${K}` | (T[K] extends object ? `${K}.${NestedKeyOf<T[K]>}` : never)
    : never

type ExtractKeys<T> = T extends object
    ? {
        [K in keyof T & string]:
        | K
        | (T[K] extends object ? `${K}.${ExtractKeys<T[K]>}` : K);
    }[keyof T & string]
    : never;

export function keyOfChecker<T>() {
    return function (s: keyof T) {
        return s.toString();
    }
}

export function keyOfCheckerNested<T>() {
    return function (s: FieldPath<T>) {
        return s.toString();
    }
}

// export function parseDataUrl(dataUrl: string): { mimeType: string; contents: string } | null {
//     const matches = dataUrl.match(/^data:(.*?);base64,(.*)$/);
//     if (!matches) return null;

//     const mimeType = matches[1];
//     const contents = atob(matches[2]);
//     return { mimeType, contents };
// }


export function groupByMap(collection, iteratee = null) {
    if (iteratee === null) {
        iteratee = (entity) => { return entity.identity; };
    }

    let grouped = new Map();

    for (let entity of collection) {
        const key = iteratee(entity);

        if (grouped.has(key)) {
            grouped.get(key).push(entity);
        } else {
            grouped.set(key, [entity]);
        }
    }

    return grouped;
}

export function keyByMap(collection, iteratee = null) {
    if (iteratee === null) {
        iteratee = (entity) => { return entity.identity; };
    }

    let keyed = new Map();

    for (let entity of collection) {
        const key = iteratee(entity);

        keyed.set(key, entity);
    }

    return keyed;
}

type SuccessResult<T> = readonly [T, null];
type ErrorResult<E = Error> = readonly [null, E];
export type Result<T, E = Error> = SuccessResult<T> | ErrorResult<E>;

export async function tryCatch<T, E = Error>(promise: Promise<T>): Promise<Result<T, E>> {
    try {
        const result = await promise;
        return [result, null] as const;
    } catch (error) {
        return [null, error as E] as const;
    }
}

export function getRandomInt(min: number, max: number) {
    const minCeiled = Math.ceil(min);
    const maxFloored = Math.floor(max);
    return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled); // The maximum is exclusive and the minimum is inclusive
}

const nanoid1 = customAlphabet('1234567890', 5)
export const getRandomId = () => parseInt(nanoid1())
export const getRandomNegativeId = () => -parseInt(nanoid1())